"use client"

import * as React from "react"

import {
  ActionCard,
  type ActionCardHeadline,
  type ActionItem,
} from "@/components/action-card"
import type { Invoice } from "@/features/invoices/components/InvoiceTable"
import { startOfCurrentWeek } from "@/features/invoices/filters"
import { useCollections, useInvoices } from "@/features/invoices/hooks"
import type { CollectionNetOut } from "@/features/invoices/types"

const MS_PER_DAY = 24 * 60 * 60 * 1000

function formatRelativePaid(paidAt: Date, now: Date): string {
  const diffDays = Math.floor((now.getTime() - paidAt.getTime()) / MS_PER_DAY)
  if (diffDays <= 0) return "Paid today"
  if (diffDays === 1) return "Paid 1d ago"
  if (diffDays < 7) return "Paid " + diffDays + "d ago"
  return "Paid " + paidAt.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

/**
 * Pick the currency whose net drives the card headline (OQ-5): USD when
 * present, else the currency with the largest absolute net this week. The
 * backend already sorts USD-first, but falls back to alphabetical (not
 * magnitude), so the "largest" tie-break is applied here.
 */
function pickPrimaryNet(
  nets: CollectionNetOut[],
): CollectionNetOut | undefined {
  if (nets.length === 0) return undefined
  const usd = nets.find((n) => n.currency === "usd")
  if (usd) return usd
  return nets.reduce((max, n) =>
    Math.abs(Number(n.net)) > Math.abs(Number(max.net)) ? n : max,
  )
}

function formatOverdueDetail(invoice: Invoice, now: Date): string {
  const todayMidnight = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  )
  const expiration = new Date(
    invoice.expirationDate.getFullYear(),
    invoice.expirationDate.getMonth(),
    invoice.expirationDate.getDate(),
  )
  const days = Math.floor(
    (todayMidnight.getTime() - expiration.getTime()) / MS_PER_DAY,
  )
  if (days <= 0) return "Due today"
  if (days === 1) return "1 day overdue"
  return days + " days overdue"
}

function customerLabel(invoice: Invoice): string {
  return invoice.customer.name || invoice.customer.email
}

function errorMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message
  return "Failed to load"
}

export function HomeActionCards() {
  const now = React.useMemo(() => new Date(), [])
  const weekStartIso = React.useMemo(
    () => startOfCurrentWeek(now).toISOString(),
    [now],
  )

  const overdueQuery = useInvoices({
    status: "overdue",
    createdBy: "me",
    page: 1,
    pageSize: 3,
  })

  const viewedQuery = useInvoices({
    status: "viewed",
    createdBy: "me",
    page: 1,
    pageSize: 3,
  })

  // Every payment collected this week — one-time full, each installment
  // charge, each deposit leg — net of in-week refunds, off the Event ledger
  // (not a status=PAID invoice query, which would miss partial settlements).
  const paidQuery = useCollections({
    createdBy: "me",
    collectedFrom: weekStartIso,
    limit: 3,
  })

  const overdueItems: ActionItem[] =
    overdueQuery.data?.items.map((inv) => ({
      id: inv.id,
      customer: customerLabel(inv),
      amount: inv.netAmount,
      currency: inv.currency,
      detail: formatOverdueDetail(inv, now),
    })) ?? []

  const viewedItems: ActionItem[] =
    viewedQuery.data?.items.map((inv) => ({
      id: inv.id,
      customer: customerLabel(inv),
      amount: inv.netAmount,
      currency: inv.currency,
      detail: inv.course,
    })) ?? []

  const paidItems: ActionItem[] =
    paidQuery.data?.items.map((collection) => ({
      // One invoice can appear twice (two charges this week), so the key is
      // composite while the link still points at the invoice.
      id: `${collection.invoice_id}:${collection.collected_at}`,
      href: `/invoices/${collection.invoice_id}`,
      customer: collection.customer_name || collection.customer_email,
      amount: Number(collection.amount),
      currency: collection.currency,
      detail: formatRelativePaid(new Date(collection.collected_at), now),
    })) ?? []

  const paidCount = paidQuery.data?.total_count ?? 0
  const primaryNet = paidQuery.data
    ? pickPrimaryNet(paidQuery.data.net_totals)
    : undefined
  // Only headline a week that actually collected something. A refund-only
  // week nets negative with total_count 0 — showing "-$500 / 0 payments"
  // next to the "No payments this week" empty state would contradict itself.
  const paidHeadline: ActionCardHeadline | undefined =
    primaryNet && paidCount > 0
      ? {
          amount: Number(primaryNet.net),
          currency: primaryNet.currency,
          extraCurrencies: (paidQuery.data?.net_totals.length ?? 1) - 1,
          subtitle: `${paidCount} ${paidCount === 1 ? "payment" : "payments"}`,
        }
      : undefined

  // The combined-status "View all" is enabled by the multi-select filter:
  // both paid and partially-paid invoices this week. The `/invoices` page
  // reads the compact `updated` preset token (not `updated_at_from`), whose
  // "this_week" resolves to the same start-of-week instant this card uses.
  const paidHref =
    "/invoices?status=paid&status=partially_paid&created_by=me&updated=this_week"

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
      <ActionCard
        title="Overdue"
        count={overdueQuery.data?.count ?? 0}
        items={overdueItems}
        viewAllHref="/invoices?status=overdue&created_by=me"
        variant="overdue"
        emptyMessage="No overdue invoices"
        loading={overdueQuery.isPending}
        error={
          overdueQuery.error ? errorMessage(overdueQuery.error) : undefined
        }
      />
      <ActionCard
        title="Viewed, not paid"
        count={viewedQuery.data?.count ?? 0}
        items={viewedItems}
        viewAllHref="/invoices?status=viewed&created_by=me"
        variant="viewed"
        emptyMessage="No viewed invoices awaiting payment"
        loading={viewedQuery.isPending}
        error={viewedQuery.error ? errorMessage(viewedQuery.error) : undefined}
      />
      <ActionCard
        title="Paid this week"
        count={paidCount}
        items={paidItems}
        headline={paidHeadline}
        viewAllHref={paidHref}
        variant="paid"
        emptyMessage="No payments this week"
        loading={paidQuery.isPending}
        error={paidQuery.error ? errorMessage(paidQuery.error) : undefined}
      />
    </div>
  )
}
